// Single source of truth for Site Policy / Terms of Use.
// Used by both the public /policy page and the first-hive acceptance dialog.

export const POLICY_VERSION = "1.0";
export const POLICY_EFFECTIVE_DATE = "July 2025";

export const POLICY_SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: [
      "By creating an account, accessing, or using Hive @ Buzzzillion (the \u201cService\u201d), you agree to be bound by these Terms of Use and our Acceptable Use Policy (together, the \u201cTerms\u201d). If you do not agree to these Terms in their entirety, you must not access or use the Service.",
      "You confirm that you are at least 13 years of age (or the minimum age required in your jurisdiction) and that you have the legal capacity to enter into this agreement.",
    ],
  },
  {
    title: "2. Purpose of the Service",
    body: [
      "Hive @ Buzzzillion is a journaling and management platform built exclusively for beekeepers. It is intended for tracking hives, recording inspections, sharing honey and bee\u2011related photos, diagnosing hive health, exchanging knowledge with other beekeepers, and similar bee\u2011related activities.",
    ],
  },
  {
    title: "3. Content Policy \u2014 Bee\u2011Related Content Only",
    body: [
      "All user\u2011generated content \u2014 including photographs, videos, inspection notes, forum posts, hive names, profile information, and any uploaded files \u2014 must be directly related to bees, beekeeping, apiary management, pollinators, or the products of the hive (honey, wax, propolis, pollen, royal jelly).",
      "Off\u2011topic content, including but not limited to personal selfies unrelated to beekeeping, promotional material unrelated to bees, memes, advertisements, or generic photography, is not permitted and may be removed at our sole discretion without notice.",
    ],
  },
  {
    title: "4. Prohibited Content & Conduct",
    body: [
      "You agree NOT to upload, post, transmit, share, or otherwise make available any content that:",
      "\u2022 Is harmful, abusive, threatening, harassing, defamatory, or otherwise objectionable;",
      "\u2022 Is illegal, illicit, or promotes illegal activity under any applicable local, state, national, or international law;",
      "\u2022 Is offensive, obscene, pornographic, sexually explicit, hateful, or discriminatory on the basis of race, ethnicity, religion, gender, sexual orientation, disability, age, or any protected class;",
      "\u2022 Depicts cruelty to animals, including but not limited to harming bees, pollinators, or other wildlife;",
      "\u2022 Contains malware, viruses, or any code designed to disrupt, damage, or gain unauthorized access to systems;",
      "\u2022 Infringes any patent, trademark, trade secret, copyright, right of publicity, or other intellectual property right of any party;",
      "\u2022 Impersonates any person or entity or misrepresents your affiliation with a person or entity;",
      "\u2022 Constitutes unsolicited or unauthorized advertising, promotional materials, junk mail, spam, or chain letters;",
      "\u2022 Solicits personal information from minors or otherwise endangers minors.",
    ],
  },
  {
    title: "5. Enforcement & Legal Consequences",
    body: [
      "Violations of this Content Policy will result in immediate removal of the offending content and may result in suspension or permanent termination of your account, without refund and without prior notice.",
      "WE RESERVE THE RIGHT TO REPORT ANY ILLEGAL, ILLICIT, OR HARMFUL ACTIVITY TO THE APPROPRIATE LAW ENFORCEMENT AUTHORITIES, AND VIOLATIONS WILL BE PROSECUTED TO THE FULLEST EXTENT OF THE LAW.",
      "You agree to cooperate fully with any investigation conducted by Hive @ Buzzzillion or by law enforcement, and you acknowledge that we may preserve and disclose your information if required to do so by law or in good\u2011faith belief that such action is necessary.",
    ],
  },
  {
    title: "6. User Accounts & Security",
    body: [
      "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use of your account or any other breach of security.",
      "You agree to provide accurate, current, and complete information during registration and to keep that information up to date.",
    ],
  },
  {
    title: "7. License & Ownership of Your Content",
    body: [
      "You retain ownership of the content you upload. By uploading content, you grant Hive @ Buzzzillion a worldwide, non\u2011exclusive, royalty\u2011free license to host, store, reproduce, display, and distribute that content solely for the purpose of operating, providing, and improving the Service.",
      "You represent and warrant that you have all necessary rights to grant this license and that your content does not violate the rights of any third party.",
    ],
  },
  {
    title: "8. Disclaimers",
    body: [
      "The Service, including any AI\u2011assisted diagnosis features, is provided \u201cAS IS\u201d and \u201cAS AVAILABLE\u201d without warranties of any kind, express or implied. AI suggestions are informational only and are not a substitute for professional veterinary, agricultural, or apiculture advice.",
      "We do not guarantee uninterrupted access to the Service and are not responsible for any loss of data resulting from the use of the Service.",
    ],
  },
  {
    title: "9. Limitation of Liability",
    body: [
      "To the maximum extent permitted by law, Hive @ Buzzzillion, its operators, affiliates, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or goodwill, arising out of or relating to your use of the Service.",
    ],
  },
  {
    title: "10. Indemnification",
    body: [
      "You agree to indemnify and hold harmless Hive @ Buzzzillion, its operators, and affiliates from any claims, damages, liabilities, costs, and expenses (including reasonable attorneys\u2019 fees) arising out of your use of the Service, your content, or your violation of these Terms.",
    ],
  },
  {
    title: "11. Termination",
    body: [
      "We may suspend or terminate your access to the Service at any time, with or without notice, for any reason, including violation of these Terms. Upon termination, your right to use the Service will immediately cease.",
    ],
  },
  {
    title: "12. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. Material changes will be communicated through the Service or by email. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.",
    ],
  },
  {
    title: "13. Contact",
    body: [
      "For questions about these Terms, please contact us via the in\u2011app Support form. By checking the acceptance box and clicking the accept button, you confirm that you have read, understood, and agree to be bound by these Terms in their entirety.",
    ],
  },
];

export function PolicyBody({ className = "" }) {
  return (
    <div className={`space-y-6 text-stone-700 leading-relaxed ${className}`}>
      <div className="text-xs uppercase tracking-[0.22em] text-amber-700">
        {`Version ${POLICY_VERSION} · Effective ${POLICY_EFFECTIVE_DATE}`}
      </div>
      <p className="text-sm">
        Welcome to Hive <span className="text-yellow-500">@</span> Buzzzillion. Please read these Terms of Use and our Acceptable Use
        Policy carefully. They form a binding agreement between you and Hive @ Buzzzillion. You must read them in their entirety
        before you may use core features of the Service, including creating a hive.
      </p>
      {POLICY_SECTIONS.map((s) => (
        <section key={s.title}>
          <h2 className="font-display text-lg font-medium text-stone-900 mb-2">{s.title}</h2>
          <div className="space-y-2 text-sm">
            {s.body.map((p, i) => (
              <p key={i} className={p.startsWith("WE RESERVE") ? "text-red-700 font-medium" : ""}>
                {p}
              </p>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
